import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import LanguageOverview from '../components/translationAdmin/LanguageOverview';
import SectionReview, { type ReviewFilter } from '../components/translationAdmin/SectionReview';
import { AddLanguageForm, LanguageSettingsCard } from '../components/translationAdmin/LanguageSettings';
import TranslationHome from '../components/translationAdmin/TranslationHome';
import DocumentReview from '../components/translationAdmin/DocumentReview';
import { clearLocalizedContentCache, getDocument as getTranslatableDocument } from '../i18n/documents';
import { getSection } from '../i18n/sections';
import { setRemoteCatalog } from '../i18n/translate';
import {
  i18nErrorMessage,
  listAdminLanguages,
  listDocuments,
  listTranslations,
  saveTranslations,
  setLanguageStatus,
  type AdminLanguage,
  type LanguageStatusAction,
  type ServerDocument,
  type ServerTranslation,
  type TranslationSaveItem,
} from '../services/i18nService';
import { buildRows, countRows, type RowCounts } from '../utils/translationRows';
import { buildDocumentRows, countDocumentRows, loadEnglishHashes } from '../utils/documentRows';
import styles from '../styles/translationAdmin.module.css';

interface User {
  name: string;
  email: string;
  isAdmin?: boolean;
}

interface TranslationAdminProps {
  user: User | null;
  onLogout: () => void;
  onLogin: () => void;
}

const REVIEW_FILTERS: ReviewFilter[] = ['attention', 'missing', 'stale', 'ai_draft', 'draft', 'approved', 'all'];

function publishCatalogLocally(code: string, items: ServerTranslation[]): void {
  const messages: Record<string, string> = {};
  for (const item of items) if (item.text) messages[item.key] = item.text;
  setRemoteCatalog(code, messages);
}

/** Translation Admin — English-only admin UI for language admins and translation managers. */
const TranslationAdmin: React.FC<TranslationAdminProps> = ({ user, onLogout, onLogin }) => {
  const [params, setParams] = useSearchParams();
  const [languages, setLanguages] = useState<AdminLanguage[]>([]);
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [translations, setTranslations] = useState<ServerTranslation[] | null>(null);
  const [documents, setDocuments] = useState<ServerDocument[] | null>(null);
  const [englishHashes, setEnglishHashes] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const langParam = params.get('lang');
  const sectionParam = params.get('section');
  const docParam = params.get('doc');
  const filterParam = params.get('filter') as ReviewFilter | null;
  const queryParam = params.get('q') || '';
  const showSettings = params.get('view') === 'languages' && isManager;

  const activeLanguage = useMemo(
    () => (showSettings ? null : languages.find((l) => l.code === langParam) || null),
    [languages, langParam, showSettings]
  );
  const showHome = !showSettings && !activeLanguage;
  const languageNames = useMemo(() => {
    const names: Record<string, string> = { en: 'English' };
    for (const l of languages) names[l.code] = l.native_name || l.name;
    return names;
  }, [languages]);
  const [homeProgress, setHomeProgress] = useState<Record<string, RowCounts | null>>({});

  const updateParams = useCallback(
    (next: Record<string, string | null>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === '') merged.delete(k);
        else merged.set(k, v);
      }
      setParams(merged);
    },
    [params, setParams]
  );

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listAdminLanguages();
        if (cancelled) return;
        setLanguages(data.languages);
        setIsManager(data.is_manager);
      } catch (e) {
        if (!cancelled) setError(i18nErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const activeCode = activeLanguage?.code;
  useEffect(() => {
    if (!activeCode) return;
    let cancelled = false;
    setTranslations(null);
    (async () => {
      try {
        const items = await listTranslations(activeCode);
        if (!cancelled) setTranslations(items);
      } catch (e) {
        if (!cancelled) setError(i18nErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCode]);

  useEffect(() => {
    if (!activeCode) return;
    let cancelled = false;
    setDocuments(null);
    (async () => {
      try {
        const [docs, hashes] = await Promise.all([listDocuments(activeCode), loadEnglishHashes()]);
        if (cancelled) return;
        setDocuments(docs);
        setEnglishHashes(hashes);
      } catch (e) {
        if (!cancelled) setError(i18nErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCode]);

  useEffect(() => {
    if (!showHome || loading || languages.length === 0) return;
    let cancelled = false;
    languages.forEach((lang) => {
      listTranslations(lang.code)
        .then((items) => countRows(buildRows(items, lang.code)))
        .catch(() => null)
        .then((counts) => {
          if (!cancelled) setHomeProgress((prev) => ({ ...prev, [lang.code]: counts }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [showHome, loading, languages]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 6000);
    return () => window.clearTimeout(timer);
  }, [message]);

  const rows = useMemo(
    () => (translations && activeCode ? buildRows(translations, activeCode) : []),
    [translations, activeCode]
  );

  const documentRows = useMemo(
    () => (documents && englishHashes && activeCode ? buildDocumentRows(documents, activeCode, englishHashes) : null),
    [documents, englishHashes, activeCode]
  );

  const replaceLanguage = (lang: AdminLanguage) =>
    setLanguages((prev) => prev.map((l) => (l.code === lang.code ? { ...l, ...lang } : l)));

  const handleSave = async (items: TranslationSaveItem[]) => {
    if (!activeCode) return;
    try {
      const updated = await saveTranslations(activeCode, items);
      setTranslations(updated);
      publishCatalogLocally(activeCode, updated);
    } catch (e) {
      throw new Error(i18nErrorMessage(e));
    }
  };

  const handleStatusAction = async (action: LanguageStatusAction) => {
    if (!activeCode) return;
    setBusy(true);
    setError(null);
    try {
      const counts = countRows(rows);
      const docCounts = documentRows ? countDocumentRows(documentRows) : null;
      const updated = await setLanguageStatus(
        activeCode,
        action,
        action === 'mark_ready'
          ? {
              total: counts.total,
              approved: counts.approved,
              documents_total: docCounts?.total ?? 0,
              documents_approved: docCounts?.approved ?? 0,
            }
          : undefined
      );
      replaceLanguage(updated);
      const done: Record<LanguageStatusAction, string> = {
        mark_ready: 'Marked ready to publish.',
        reopen: 'Reopened for more work.',
        publish: 'Published — it now appears in the language menu for everyone.',
        unpublish: 'Unpublished — hidden from members again.',
      };
      setMessage(done[action]);
    } catch (e) {
      setError(i18nErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', active: false },
    ...(user?.isAdmin ? [{ label: 'Admin', path: '/admin', active: false }] : []),
    { label: 'Translations', path: '/translations', active: showHome },
    ...(activeLanguage
      ? [{ label: activeLanguage.native_name || activeLanguage.name, path: `/translations?lang=${activeLanguage.code}`, active: !sectionParam && !docParam }]
      : showSettings
        ? [{ label: 'Manage languages', path: '/translations?view=languages', active: true }]
        : []),
  ];

  const goHome = () => updateParams({ lang: null, section: null, filter: null, q: null, view: null, doc: null });
  const homeLink = (
    <div className={styles.reviewHeader}>
      <button type="button" className={styles.linkButton} onClick={goHome}>
        ← Translations home
      </button>
    </div>
  );

  const renderBody = () => {
    if (!user) {
      return (
        <div className={styles.card}>
          <p>Sign in to use Translation Admin.</p>
          <button type="button" className={styles.button} onClick={onLogin}>
            Sign in
          </button>
        </div>
      );
    }
    if (loading) return <p className={styles.muted}>Loading…</p>;
    if (!isManager && languages.length === 0) {
      return (
        <div className={styles.card}>
          <p>You haven't been assigned to a language yet. Ask the site admin to add you as a language admin.</p>
        </div>
      );
    }

    if (showSettings && isManager) {
      return (
        <>
          {homeLink}
          {languages.map((lang) => (
            <LanguageSettingsCard
              key={lang.code}
              language={lang}
              allLanguages={languages}
              onDetailsSaved={(oldCode, updated) => {
                setLanguages((prev) =>
                  prev
                    .map((l) => {
                      if (l.code === oldCode) return updated;
                      const fallbacks = (l.fallbacks || []).map((f) => (f === oldCode ? updated.code : f));
                      return { ...l, fallbacks };
                    })
                    .sort((a, b) => a.code.localeCompare(b.code))
                );
                setMessage(
                  oldCode === updated.code
                    ? `Saved ${updated.name}.`
                    : `Saved ${updated.name}. Its code is now ${updated.code}.`
                );
              }}
              onAdminsSaved={(admins) => replaceLanguage({ ...lang, admins })}
            />
          ))}
          <AddLanguageForm
            existingCodes={languages.map((l) => l.code)}
            onCreated={(lang) => {
              setLanguages((prev) => [...prev, lang].sort((a, b) => a.code.localeCompare(b.code)));
              updateParams({ view: null, lang: lang.code });
              setMessage(`Added ${lang.name}. Assign language admins under Manage languages.`);
            }}
          />
        </>
      );
    }

    if (!activeLanguage) {
      return (
        <TranslationHome
          languages={languages}
          isManager={isManager}
          userEmail={user.email}
          progress={homeProgress}
          onOpenLanguage={(code) => updateParams({ lang: code, section: null, filter: null, q: null, view: null, doc: null })}
          onManage={() => updateParams({ view: 'languages', lang: null, section: null, filter: null, q: null })}
        />
      );
    }

    if (translations === null) return <p className={styles.muted}>Loading translations…</p>;

    const languageName = activeLanguage.native_name || activeLanguage.name;

    const docRow = docParam && getTranslatableDocument(docParam) ? documentRows?.find((r) => r.doc.id === docParam) : null;
    if (docParam && getTranslatableDocument(docParam)) {
      if (!docRow) return <p className={styles.muted}>Loading document…</p>;
      return (
        <DocumentReview
          key={`${activeLanguage.code}:${docParam}`}
          row={docRow}
          languageCode={activeLanguage.code}
          languageName={languageName}
          onBack={() => updateParams({ doc: null })}
          onSaved={(doc, saved) => {
            clearLocalizedContentCache();
            setDocuments((prev) => {
              const rest = (prev || []).filter((d) => d.id !== doc.id);
              if (!saved) return rest;
              const { text: _text, ...meta } = saved;
              return [...rest, meta];
            });
          }}
        />
      );
    }

    if (sectionParam) {
      const isAll = sectionParam === 'all';
      const section = getSection(sectionParam);
      const sectionRows = isAll ? rows : rows.filter((r) => r.sectionId === section.id);
      const filter = filterParam && REVIEW_FILTERS.includes(filterParam) ? filterParam : undefined;
      return (
        <SectionReview
          key={`${activeLanguage.code}:${sectionParam}:${filter ?? ''}:${queryParam}`}
          languageName={languageName}
          title={isAll ? 'All strings' : section.label}
          description={isAll ? undefined : section.description}
          rows={sectionRows}
          initialFilter={filter}
          initialQuery={queryParam}
          onBack={() => updateParams({ section: null, filter: null, q: null })}
          onSave={handleSave}
        />
      );
    }

    return (
      <>
      {homeLink}
      <LanguageOverview
        language={activeLanguage}
        languageNames={languageNames}
        userEmail={user.email}
        rows={rows}
        documentRows={documentRows}
        isManager={isManager}
        busy={busy}
        onOpen={(sectionId, filter, query) =>
          updateParams({ section: sectionId, filter: filter ?? null, q: query ?? null })
        }
        onOpenDocument={(docId) => updateParams({ doc: docId, section: null, filter: null, q: null })}
        onStatusAction={(action) => void handleStatusAction(action)}
      />
      </>
    );
  };

  return (
    <div className={styles.page}>
      <Header breadcrumbs={breadcrumbs} user={user} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.main}>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderRow}>
            <h1>Translations</h1>
            <button type="button" className={styles.buttonSecondary} onClick={() => setGuideOpen(true)}>
              User guide
            </button>
          </div>
          {showHome && (
            <p>
              <button type="button" className={styles.linkButton} onClick={() => setGuideOpen(true)}>
                New here? Read the user guide.
              </button>
            </p>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {message && <div className={styles.success}>{message}</div>}

        {renderBody()}
      </main>
      <ContentReader
        isOpen={guideOpen}
        onClose={() => setGuideOpen(false)}
        filePath="/content/Translation-Admin-Guide.md"
        startTag=""
        endTag=""
      />
    </div>
  );
};

export default TranslationAdmin;
